import React, { useEffect, useRef } from 'react';
import styles from '../styles/SuggestionChips.module.css';

/**
 * A row of tappable conversation-suggestion chips, shown above the input bar
 * when Sophia steers the conversation (or on the first message / welcome-back).
 * Tapping a chip sends its message as a normal user turn (see useChat.sendSuggestion).
 *
 * Renders nothing when there are no suggestions.
 */
function SuggestionChips({ suggestions = [], onPick, disabled = false }) {
  const containerRef = useRef(null);

  // Chips are position:fixed and can wrap onto several lines. Publish their
  // rendered height as a CSS variable so the chat scroll area can extend its
  // bottom spacer and never hide content behind the chips.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const publishHeight = () => {
      document.documentElement.style.setProperty(
        '--suggestion-chips-height',
        `${el.offsetHeight}px`
      );
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty('--suggestion-chips-height', '0px');
    };
  }, [suggestions]);

  if (!suggestions || suggestions.length === 0) return null;

  const visibleSuggestions = suggestions.slice(0, 3);

  return (
    <div className={styles.suggestionChips} data-tour="suggestion-chips" ref={containerRef}>
      <div className={styles.scroller}>
        {visibleSuggestions.map((s) => (
          <button
            key={s.id ?? s.message}
            type="button"
            className={styles.chip}
            disabled={disabled}
            onClick={() => onPick && onPick(s)}
            title={s.message}
          >
            {s.message}
          </button>
        ))}
      </div>
    </div>
  );
}

export default SuggestionChips;
